/**
 * R-15 — semantic citation-supports-claim verifier (embedding pre-filter + Flash judge).
 */
import { Type } from '@google/genai';
import { AUDIT_CONFIG } from '../../config/auditConfig';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { DataSource, SiteRagReport } from '../../types/siteRag';
import { GEMINI_PRO_MODEL } from '../../config/geminiModels';
import { ai } from '../../services/geminiClient';
import { cosineSimilarity } from '../embeddings/cosine';
import { embedText, type EmbedTaskType } from '../embeddings/embedText';
import { tracedGeminiCall } from '../telemetry/tracedGeminiCall';
import { logAuditEvent } from '../telemetry/auditTelemetry';
import { extractSections, reportFullText } from './reportTextExtract';
import {
  buildCitationSupportJudgePrompt,
} from './prompts/citationSupportJudge';

const REF_TOKEN_RE = /\[Ref\s+(\d+)\]/gi;
const JUDGE_MODEL = GEMINI_PRO_MODEL;
const MIN_INLINE_CITATIONS = 5;
const HIGH_SIM_THRESHOLD = 0.85;
const LOW_SIM_THRESHOLD = 0.30;

export interface ClaimCitationPair {
  pairId: string;
  claimText: string;
  sectionId: string;
  refIndex: number;
  sourceText: string;
  sourceLabel: string;
  lineStart: number;
  lineEnd: number;
}

export type PrefilterDecision = 'skip_supported' | 'flag_unsupported' | 'llm_judge';

export interface PrefilterResult {
  decision: PrefilterDecision;
  similarity: number;
}

export type SupportVerdict = 'SUPPORTED' | 'UNSUPPORTED' | 'CONTRADICTED' | 'PARTIAL';

export interface SupportJudgement {
  verdict: SupportVerdict;
  rationale: string;
}

export interface CitationSupportFinding {
  pairId: string;
  claimText: string;
  refIndex: number;
  sectionId: string;
  severity: 'warn' | 'block';
  verdict: SupportVerdict;
  rationale: string;
  similarity?: number;
  route: 'embedding' | 'llm-judge';
  lineStart: number;
  lineEnd: number;
  fixHint: string;
}

export interface CitationSupportVerificationResult {
  findings: CitationSupportFinding[];
  pairsScanned: number;
  inlineCitationCount: number;
  llmCalls: number;
  embeddingCalls: number;
  skippedLowCitationCount: boolean;
  checkedAt: string;
}

export type CitationEmbedder = (text: string) => Promise<number[]>;

export type CitationSupportLlmJudge = (
  claim: string,
  source: string,
  ctx: { refIndex: number; sourceLabel: string; sectionId: string },
) => Promise<SupportJudgement | null>;

export interface VerifyCitationSupportOptions {
  maxLlmCalls?: number;
  embedder?: CitationEmbedder;
  llmJudge?: CitationSupportLlmJudge;
  siteId?: string;
  reportId?: string;
  reportType?: string;
  /** When false, always returns empty (fixture / flag-off). */
  enabled?: boolean;
}

const embeddingPairCache = new Map<string, { vector: number[]; cachedAt: number }>();
const EMBED_CACHE_TTL_MS = AUDIT_CONFIG.ACTION_EMBED_CACHE_TTL_MS;

function pairCacheKey(claim: string, source: string): string {
  let h = 0;
  const blob = `${claim}\0${source}`;
  for (let i = 0; i < blob.length; i += 1) {
    h = ((h << 5) - h + blob.charCodeAt(i)) | 0;
  }
  return `r15:${Math.abs(h)}:${blob.length}`;
}

function countInlineCitations(text: string): number {
  const seen = new Set<number>();
  for (const m of text.matchAll(new RegExp(REF_TOKEN_RE.source, 'gi'))) {
    const n = parseInt(m[1]!, 10);
    if (Number.isFinite(n)) seen.add(n);
  }
  return seen.size;
}

function extractRefIndices(text: string): number[] {
  const out = new Set<number>();
  for (const m of text.matchAll(new RegExp(REF_TOKEN_RE.source, 'gi'))) {
    const n = parseInt(m[1]!, 10);
    if (Number.isFinite(n)) out.add(n);
  }
  return [...out].sort((a, b) => a - b);
}

function splitClaimSegments(text: string): Array<{ text: string; lineStart: number; lineEnd: number }> {
  const lines = text.split('\n');
  const segments: Array<{ text: string; lineStart: number; lineEnd: number }> = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]!;
    const re = /[^.!?\n]+(?:[.!?](?:\s|$)|$)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      const chunk = m[0].trim();
      if (chunk.length >= 20 && /\[Ref\s+\d+\]/i.test(chunk)) {
        segments.push({ text: chunk, lineStart: i + 1, lineEnd: i + 1 });
      }
    }
    if (segments.every((s) => s.lineStart !== i + 1) && /\[Ref\s+\d+\]/i.test(line)) {
      const trimmed = line.trim();
      if (trimmed.length >= 15) {
        segments.push({ text: trimmed, lineStart: i + 1, lineEnd: i + 1 });
      }
    }
  }
  return segments;
}

function resolveReference(
  refs: DataSource[] | undefined,
  index: number,
): { sourceText: string; sourceLabel: string } {
  const row = refs?.[index - 1];
  if (!row) {
    return {
      sourceText: '(reference row missing)',
      sourceLabel: `Ref ${index}`,
    };
  }
  const label = [row.source, row.field].filter(Boolean).join(' — ') || `Ref ${index}`;
  const parts = [
    row.field ? `Field: ${row.field}` : '',
    row.source ? `Source: ${row.source}` : '',
    row.url ? `URL: ${row.url}` : '',
  ].filter(Boolean);

  if (row.subItems?.length) {
    for (const sub of row.subItems.slice(0, 6)) {
      parts.push(`${sub.topic}: ${sub.detail}`);
    }
  }

  return {
    sourceLabel: label,
    sourceText: parts.join('\n') || label,
  };
}

function buildFixHint(finding: Omit<CitationSupportFinding, 'fixHint'>): string {
  return `[Ref ${finding.refIndex}] in "${finding.sectionId}": ${finding.verdict} — ${finding.rationale}`;
}

/** Extract claim + cited-reference pairs from report prose. */
export function extractClaimsWithCitations(report: SiteRagReport): ClaimCitationPair[] {
  const refs = report.references ?? [];
  const pairs: ClaimCitationPair[] = [];
  const seen = new Set<string>();

  const sections = extractSections(report);
  const fullText = reportFullText(report);
  const sectionSources = sections.length > 0
    ? sections
    : [{ id: 'body', content: fullText }];

  for (const section of sectionSources) {
    for (const segment of splitClaimSegments(section.content)) {
      for (const refIndex of extractRefIndices(segment.text)) {
        const { sourceText, sourceLabel } = resolveReference(refs, refIndex);
        const pairId = `${section.id}:${refIndex}:${segment.lineStart}:${segment.text.slice(0, 40)}`;
        if (seen.has(pairId)) continue;
        seen.add(pairId);
        pairs.push({
          pairId,
          claimText: segment.text,
          sectionId: section.id,
          refIndex,
          sourceText,
          sourceLabel,
          lineStart: segment.lineStart,
          lineEnd: segment.lineEnd,
        });
      }
    }
  }

  return pairs;
}

/** Cosine pre-filter: high similarity skips LLM; low flags unsupported; mid → LLM. */
export async function prefilterWithEmbeddings(
  pair: ClaimCitationPair,
  embedder: CitationEmbedder,
): Promise<PrefilterResult> {
  const key = pairCacheKey(pair.claimText, pair.sourceText);
  let claimVec: number[] | undefined;
  let sourceVec: number[] | undefined;

  const cachedClaim = embeddingPairCache.get(key);
  const cachedSource = embeddingPairCache.get(`${key}:src`);
  if (cachedClaim && cachedSource && Date.now() - cachedClaim.cachedAt < EMBED_CACHE_TTL_MS) {
    claimVec = cachedClaim.vector;
    sourceVec = cachedSource.vector;
  } else {
    [claimVec, sourceVec] = await Promise.all([
      embedder(pair.claimText),
      embedder(pair.sourceText),
    ]);
    if (claimVec.length > 0) {
      embeddingPairCache.set(key, { vector: claimVec, cachedAt: Date.now() });
    }
    if (sourceVec.length > 0) {
      embeddingPairCache.set(`${key}:src`, { vector: sourceVec, cachedAt: Date.now() });
    }
  }

  const similarity = cosineSimilarity(claimVec ?? [], sourceVec ?? []);
  if (similarity >= HIGH_SIM_THRESHOLD) {
    return { decision: 'skip_supported', similarity };
  }
  if (similarity < LOW_SIM_THRESHOLD) {
    return { decision: 'flag_unsupported', similarity };
  }
  return { decision: 'llm_judge', similarity };
}

function parseJudgeResponse(text: string | undefined | null): SupportJudgement | null {
  if (!text?.trim()) return null;
  try {
    const parsed = JSON.parse(text) as { verdict?: string; rationale?: string };
    const raw = String(parsed.verdict ?? '').toUpperCase();
    const verdict = (['SUPPORTED', 'UNSUPPORTED', 'CONTRADICTED', 'PARTIAL'] as const).find((v) => v === raw);
    if (!verdict) return null;
    return {
      verdict,
      rationale: typeof parsed.rationale === 'string' ? parsed.rationale.trim() : '',
    };
  } catch {
    return null;
  }
}

function severityForVerdict(verdict: SupportVerdict): 'warn' | 'block' | null {
  if (verdict === 'CONTRADICTED') return 'block';
  if (verdict === 'UNSUPPORTED' || verdict === 'PARTIAL') return 'warn';
  return null;
}

/** Default Flash judge — overridable in tests; failures return null (no finding). */
export const defaultCitationSupportJudge: CitationSupportLlmJudge = async (claim, source, ctx) => {
  const prompt = buildCitationSupportJudgePrompt({
    claimText: claim,
    sourceText: source,
    sourceLabel: ctx.sourceLabel,
    refIndex: ctx.refIndex,
    sectionId: ctx.sectionId,
  });
  try {
    const response = await tracedGeminiCall(
      JUDGE_MODEL,
      () => ai.models.generateContent({
        model: JUDGE_MODEL,
        contents: prompt,
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              verdict: {
                type: Type.STRING,
                description: 'SUPPORTED | UNSUPPORTED | CONTRADICTED | PARTIAL',
              },
              rationale: { type: Type.STRING, description: 'One-sentence justification' },
            },
            required: ['verdict', 'rationale'],
          },
        },
      }),
      { stage: 'citation-support-judge' },
    );
    return parseJudgeResponse((response as { text?: string }).text);
  } catch (err) {
    console.warn('[citationSemanticVerifier] judge failed:', (err as Error)?.message);
    return null;
  }
};

/** Flash JSON judge for ambiguous embedding band. */
export async function judgeCitationSupport(
  claim: string,
  source: string,
  llm: CitationSupportLlmJudge,
  ctx: { refIndex: number; sourceLabel: string; sectionId: string },
): Promise<SupportJudgement | null> {
  return llm(claim, source, ctx);
}

export function buildCitationSupportFixBlock(findings: readonly CitationSupportFinding[]): string {
  if (findings.length === 0) return '';
  const lines = [
    'CITATION SUPPORT FIX: Prior audit found claim/source mismatches. Correct or recite each before finishing:',
  ];
  for (const f of findings.slice(0, 25)) {
    lines.push(`- ${f.fixHint}`);
  }
  if (findings.length > 25) {
    lines.push(`- …and ${findings.length -  25} more citation-support finding(s).`);
  }
  return lines.join('\n');
}

export function defaultCitationEmbedder(taskType: EmbedTaskType = 'SEMANTIC_SIMILARITY'): CitationEmbedder {
  return (text: string) => embedText(text, { taskType });
}

/** Reset embedding cache — test helper. */
export function resetCitationSupportEmbedCache(): void {
  embeddingPairCache.clear();
}

/** Verify cited evidence supports surrounding claims. Skips when inline citations < 5. */
export async function verifyCitationSupport(
  report: SiteRagReport,
  _evidence: SiteEvidenceDocument | null | undefined,
  opts: VerifyCitationSupportOptions = {},
): Promise<CitationSupportVerificationResult> {
  const enabled = opts.enabled ?? AUDIT_CONFIG.ENABLE_CITATION_SUPPORT_VERIFICATION;
  const empty: CitationSupportVerificationResult = {
    findings: [],
    pairsScanned: 0,
    inlineCitationCount: 0,
    llmCalls: 0,
    embeddingCalls: 0,
    skippedLowCitationCount: false,
    checkedAt: new Date().toISOString(),
  };
  if (!enabled) return empty;

  const bodyText = reportFullText(report);
  const inlineCitationCount = countInlineCitations(bodyText);
  if (inlineCitationCount < MIN_INLINE_CITATIONS) {
    return { ...empty, inlineCitationCount, skippedLowCitationCount: true };
  }

  const embedder = opts.embedder ?? defaultCitationEmbedder();
  const llmJudge = opts.llmJudge ?? defaultCitationSupportJudge;
  const maxLlmCalls = opts.maxLlmCalls ?? AUDIT_CONFIG.CITATION_SUPPORT_MAX_LLM_CALLS;

  const rawPairs = extractClaimsWithCitations(report).map((pair) => {
    const resolved = resolveReference(report.references, pair.refIndex);
    return { ...pair, sourceText: resolved.sourceText, sourceLabel: resolved.sourceLabel };
  });

  const findings: CitationSupportFinding[] = [];
  let llmCalls = 0;
  let embeddingCalls = 0;

  for (const pair of rawPairs) {
    const pre = await prefilterWithEmbeddings(pair, embedder);
    embeddingCalls += 1;

    if (pre.decision === 'skip_supported') continue;

    if (pre.decision === 'flag_unsupported') {
      const base = {
        pairId: pair.pairId,
        claimText: pair.claimText,
        refIndex: pair.refIndex,
        sectionId: pair.sectionId,
        severity: 'warn' as const,
        verdict: 'UNSUPPORTED' as const,
        rationale: `Embedding similarity ${pre.similarity.toFixed(2)} below ${LOW_SIM_THRESHOLD} — source appears unrelated.`,
        similarity: pre.similarity,
        route: 'embedding' as const,
        lineStart: pair.lineStart,
        lineEnd: pair.lineEnd,
      };
      findings.push({ ...base, fixHint: buildFixHint(base) });
      continue;
    }

    if (llmCalls >= maxLlmCalls) continue;

    llmCalls += 1;
    const judgement = await judgeCitationSupport(
      pair.claimText,
      pair.sourceText,
      llmJudge,
      {
        refIndex: pair.refIndex,
        sourceLabel: pair.sourceLabel,
        sectionId: pair.sectionId,
      },
    );
    if (!judgement) continue;

    const severity = severityForVerdict(judgement.verdict);
    if (!severity) continue;

    const base = {
      pairId: pair.pairId,
      claimText: pair.claimText,
      refIndex: pair.refIndex,
      sectionId: pair.sectionId,
      severity,
      verdict: judgement.verdict,
      rationale: judgement.rationale || `${judgement.verdict} per LLM judge`,
      similarity: pre.similarity,
      route: 'llm-judge' as const,
      lineStart: pair.lineStart,
      lineEnd: pair.lineEnd,
    };
    findings.push({ ...base, fixHint: buildFixHint(base) });
  }

  logAuditEvent({
    type: 'citation-support-verification-completed',
    siteId: opts.siteId ?? report.siteId,
    reportId: opts.reportId ?? report.id,
    reportType: opts.reportType ?? report.reportType,
    payload: {
      inlineCitationCount,
      pairsScanned: rawPairs.length,
      findingCount: findings.length,
      llmCalls,
      embeddingCalls,
      skippedLowCitationCount: false,
    },
  });

  return {
    findings,
    pairsScanned: rawPairs.length,
    inlineCitationCount,
    llmCalls,
    embeddingCalls,
    skippedLowCitationCount: false,
    checkedAt: new Date().toISOString(),
  };
}
